import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Building2, User, BadgeCheck, MapPin, Package, Globe, Calendar, Star, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ProductCard } from "@/components/ProductCard";
import { LoadingState, EmptyState } from "@/components/States";
import { Stars } from "@/components/Stars";
import { formatDate, timeAgo } from "@/lib/format";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function SellerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [eligible, setEligible] = useState(false);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadReviews = () => api.get(`/sellers/${id}/reviews`).then(({ data }) => setReviews(data));

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api.get(`/sellers/${id}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
    loadReviews();
  }, [id]);

  useEffect(() => {
    if (user && user.user_id !== id) {
      api.get(`/sellers/${id}/can-review`).then(({ data }) => { setEligible(data.eligible); setExisting(data.existing); }).catch(() => {});
    }
  }, [user, id]);

  if (loading) return <LoadingState />;
  if (!data) return <div className="py-20 text-center text-slate-500">Satıcı bulunamadı.</div>;

  const s = data.seller;
  const isCorp = s.account_type === "kurumsal";
  const rating = s.rating || { average: 0, count: 0 };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="seller-profile-header">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
            {isCorp ? <Building2 size={30} /> : <User size={30} />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl font-extrabold text-slate-900">{s.name}</h1>
              {s.corporate_approved && <BadgeCheck size={22} className="shrink-0 text-emerald-500" />}
            </div>
            <p className="mt-0.5 text-sm font-medium text-emerald-600">
              {isCorp ? (s.corporate_approved ? "Onaylı Kurumsal Satıcı" : "Kurumsal Satıcı") : "Bireysel Satıcı"}
            </p>
            {rating.count > 0 && (
              <div className="mt-2 flex items-center gap-2" data-testid="seller-rating">
                <Stars value={rating.average} />
                <span className="text-sm font-semibold text-slate-700">{rating.average}</span>
                <span className="text-sm text-slate-400">({rating.count} değerlendirme)</span>
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-500">
              {s.city && <span className="flex items-center gap-1"><MapPin size={15} />{s.city}{s.district ? ` / ${s.district}` : ""}</span>}
              <span className="flex items-center gap-1"><Package size={15} />{data.count} aktif ilan</span>
              <span className="flex items-center gap-1"><Calendar size={15} />Üyelik: {formatDate(s.created_at)}</span>
              {s.company?.website && (
                <a href={s.company.website.startsWith("http") ? s.company.website : `https://${s.company.website}`}
                  target="_blank" rel="noreferrer" className="flex items-center gap-1 text-emerald-700 hover:underline">
                  <Globe size={15} />Web Sitesi
                </a>
              )}
            </div>
          </div>
        </div>
        {isCorp && s.company?.company_description && (
          <p className="mt-4 border-t border-slate-100 pt-4 text-sm text-slate-600">{s.company.company_description}</p>
        )}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="mb-5 font-display text-xl font-extrabold text-slate-900">İlanları</h2>
          {data.listings.length === 0 ? (
            <EmptyState title="Bu satıcının aktif ilanı yok" icon={Package} />
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {data.listings.map((l) => <ProductCard key={l.listing_id} listing={l} />)}
            </div>
          )}
        </div>

        <div>
          <h2 className="mb-5 font-display text-xl font-extrabold text-slate-900">Değerlendirmeler</h2>
          {eligible && <ReviewForm sellerId={id} existing={existing} onDone={() => { loadReviews(); api.get(`/sellers/${id}`).then(({ data }) => setData(data)); }} />}
          {reviews === null ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : reviews.items.length === 0 ? (
            <p className="text-sm text-slate-500">Henüz değerlendirme yok.</p>
          ) : (
            <div className="space-y-3">
              {reviews.items.map((r) => (
                <div key={r.review_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`review-${r.review_id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800">{r.buyer_name}</span>
                    <Stars value={r.rating} size={14} />
                  </div>
                  {r.comment && <p className="mt-1.5 text-sm text-slate-600">{r.comment}</p>}
                  <p className="mt-1 text-xs text-slate-400">{timeAgo(r.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewForm({ sellerId, existing, onDone }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [comment, setComment] = useState(existing?.comment || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!rating) { toast.error("Lütfen puan verin"); return; }
    setSaving(true);
    try {
      await api.post(`/sellers/${sellerId}/reviews`, { rating, comment });
      toast.success("Değerlendirmeniz kaydedildi");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="review-form">
      <p className="text-sm font-semibold text-slate-800">{existing ? "Değerlendirmenizi güncelleyin" : "Bu satıcıyı değerlendirin"}</p>
      <div className="mt-2 flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} data-testid={`rate-${n}`}>
            <Star size={24} className={n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
          </button>
        ))}
      </div>
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Deneyiminizi paylaşın (opsiyonel)" className="mt-2" data-testid="review-comment" />
      <Button onClick={submit} disabled={saving} className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700" data-testid="review-submit">
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{existing ? "Güncelle" : "Gönder"}
      </Button>
    </div>
  );
}
